/**
 * Keeps every free shipping bar in step with the cart.
 *
 * The bar is rendered in Liquid, so it is only correct until the cart changes. Sections that
 * re-render on a cart update (the drawer) get a fresh bar for free; the ones that do not (the
 * product page) are updated here, from the cart the update event carries.
 */
import { StandardEvents } from '@shopify/events';

/**
 * Formats an amount in cents the way the storefront writes money.
 *
 * @param {number} cents - The amount, in the shop currency's smallest unit.
 * @param {string} currency - ISO currency code, e.g. 'EUR'.
 * @returns {string} The formatted amount.
 */
function money(cents, currency) {
  const locale = document.documentElement.lang || 'nl';

  try {
    // The Liquid `money` filter writes €70,00 without a gap, so drop the separator Intl inserts.
    return new Intl.NumberFormat(locale, { style: 'currency', currency })
      .format(cents / 100)
      .replace(/ /g, '');
  } catch {
    return (cents / 100).toFixed(2);
  }
}

/**
 * Repaints every bar on the page for the given cart total.
 *
 * @param {number} total - The cart total, in cents.
 * @param {string} currency - ISO currency code of that total.
 */
function render(total, currency) {
  for (const bar of document.querySelectorAll('[data-shipping-progress]')) {
    const threshold = Number(bar.getAttribute('data-threshold'));
    if (!threshold) continue;

    const missing = threshold - total;
    const text = bar.querySelector('[data-shipping-progress-text]');
    const fill = bar.querySelector('[data-shipping-progress-bar]');

    if (text) {
      text.textContent =
        missing > 0 ? `Nog ${money(missing, currency)} tot gratis verzending` : 'Je hebt gratis verzending';
    }

    const amount = bar.querySelector('[data-shipping-progress-amount]');
    if (amount) {
      amount.toggleAttribute('hidden', missing <= 0);
    }

    fill?.style.setProperty('--progress', `${Math.min(Math.round((total / threshold) * 100), 100)}%`);
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(async ({ detail }) => {
      // Prefer the cart the dispatcher already fetched; fall back to asking for it ourselves.
      const cart =
        detail?.cart?.cost?.totalAmount != null
          ? {
              total: Math.round(Number(detail.cart.cost.totalAmount.amount) * 100),
              currency: detail.cart.cost.totalAmount.currencyCode,
            }
          : await fetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } })
              .then((response) => response.json())
              .then((json) => ({ total: json.total_price, currency: json.currency }));

      render(cart.total, cart.currency);
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[shipping-progress] Cart update rejected:', error);
    });
});
